import { createClient, User } from '@supabase/supabase-js';
import { API_CONFIG } from '../config/api';

const supabaseUrl = API_CONFIG.SUPABASE_URL;
const supabaseAnonKey = API_CONFIG.SUPABASE_ANON_KEY;

console.log('🔧 Supabase配置检查:');
console.log('URL:', supabaseUrl ? '✅ 已设置' : '❌ 未设置');
console.log('ANON_KEY:', supabaseAnonKey ? '✅ 已设置' : '❌ 未设置');

if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = 'Missing Supabase environment variables';
  console.error('❌', errorMsg);
  console.error('请检查.env文件是否存在并包含以下变量:');
  console.error('- VITE_SUPABASE_URL');
  console.error('- VITE_SUPABASE_ANON_KEY');
  throw new Error(errorMsg);
}

console.log('🚀 创建Supabase客户端...');
export const supabase = createClient(supabaseUrl, supabaseAnonKey);
console.log('✅ Supabase客户端创建成功');

// Cache for admin status
let cachedAdmin: boolean | null = null;
let adminPromise: Promise<boolean> | null = null;

// localStorage key for admin status with TTL
const ADMIN_CACHE_KEY = 'softskill_admin';
const ADMIN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Helper function to get admin status from localStorage with TTL check
const getAdminFromStorage = (): boolean | null => {
  try {
    const stored = localStorage.getItem(ADMIN_CACHE_KEY);
    if (!stored) return null;
    
    const { value, ts } = JSON.parse(stored);
    const now = Date.now();
    
    // Check if the stored value is still valid (less than 5 minutes old)
    if (now - ts < ADMIN_CACHE_TTL) {
      return value;
    } else {
      // Remove expired entry
      localStorage.removeItem(ADMIN_CACHE_KEY);
      return null;
    }
  } catch (error) {
    console.error('Error reading admin status from localStorage:', error);
    localStorage.removeItem(ADMIN_CACHE_KEY);
    return null;
  }
};

// Helper function to store admin status in localStorage with timestamp
const setAdminInStorage = (value: boolean): void => {
  try {
    const data = {
      value,
      ts: Date.now()
    };
    localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error storing admin status in localStorage:', error);
  }
};

// 检查是否为管理员 - 调用后端 Edge Function
export const isAdmin = async (user?: User | null, forceRefresh = false): Promise<boolean> => {
  // If not forcing refresh, try to get from localStorage first
  if (!forceRefresh) {
    const storedAdmin = getAdminFromStorage();
    if (storedAdmin !== null) {
      cachedAdmin = storedAdmin;
      return storedAdmin;
    }
  }

  // If we have a cached result and not forcing refresh, return it immediately
  if (cachedAdmin !== null && !forceRefresh) {
    return cachedAdmin;
  }

  // If there's already a request in flight, return that promise
  if (adminPromise && !forceRefresh) {
    return adminPromise;
  }

  // Create new promise for admin check
  adminPromise = (async (): Promise<boolean> => {
    try {
      // If no user provided, return false (not admin)
      if (!user) {
        cachedAdmin = false;
        setAdminInStorage(false);
        return false;
      }

      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('获取 session 失败:', sessionError);
        cachedAdmin = false;
        setAdminInStorage(false);
        return false;
      }

      // 调用后端 Edge Function 检查管理员权限
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/check-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('管理员权限检查请求失败:', response.status, response.statusText);
        cachedAdmin = false;
        setAdminInStorage(false);
        return false;
      }

      const result = await response.json();
      console.log('check-admin API 原始响应:', result);
      
      // 检查响应格式，支持两种格式：直接返回或包装在data中
      let adminData, isAdminUser;
      
      if (result.success && result.data) {
        // 新格式：{success: true, data: {isAdmin: true, email: "...", userId: "..."}}
        adminData = result.data;
        isAdminUser = adminData.isAdmin || false;
      } else if (result.isAdmin !== undefined) {
        // 旧格式：{isAdmin: true, email: "...", userId: "..."}
        adminData = result;
        isAdminUser = result.isAdmin || false;
      } else {
        // 未知格式，默认为非管理员
        console.warn('未知的 check-admin 响应格式:', result);
        adminData = {};
        isAdminUser = false;
      }
      
      // Cache the result in memory and localStorage
      cachedAdmin = isAdminUser;
      setAdminInStorage(isAdminUser);
      
      console.log('管理员权限检查结果:', { 
        email: adminData.email, 
        isAdmin: isAdminUser,
        rawResponse: result 
      });
      return isAdminUser;
    } catch (error) {
      // On error, don't cache and return false
      console.error('Error checking admin status:', error);
      cachedAdmin = false;
      setAdminInStorage(false);
      return false;
    } finally {
      // Clear the in-flight promise
      adminPromise = null;
    }
  })();

  return adminPromise;
};

// Clear admin cache (useful when user changes or signs out)
export const clearAdminCache = (): void => {
  cachedAdmin = null;
  adminPromise = null;
  // Also clear localStorage
  try {
    localStorage.removeItem(ADMIN_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing admin cache from localStorage:', error);
  }
};

// 获取当前用户
const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};